import { redactTokensRecursive } from '@cash-web/shared/util-redaction'
import { uuidv4 } from '@cash-web/shared-util-formatting'
import type { MessageContext } from '@cash-web/shared-util-protos/squareup/cash/cdpproxy/api/message_context.pb'
import { OperatingSystemName } from '@cash-web/shared-util-protos/squareup/cash/cdpproxy/api/operating_system.pb'
import { getDeviceInfo } from '@cash-web/shared-util-user-agent'
import type { Event } from '@squareup/customer-data-events-web'

interface logEventParams {
  event: Event
  region: string
  hostName?: string
  customerToken?: string
}

export async function logEvent({ event, region, hostName, customerToken }: logEventParams) {
  const endpoint = `${hostName ?? ''}/cdp/track`
  const data = {
    context: getAnalyticsEventContext(region),
    track: {
      event_name: event.name,
      properties: JSON.stringify(event.parameters),
    },
    message_uuid: uuidv4(),
    timestamp_since_epoch_millis: Date.now(),
    customer_token: customerToken,
  }
  redactTokensRecursive(data)
  try {
    const headers = { type: 'application/json; charset=utf-8' }
    const blob = new Blob([JSON.stringify(data)], headers)
    navigator?.sendBeacon(endpoint, blob)
  } catch (e) {
    // Defer import until client side render
    const { post } = await import('@cash-web/shared-util-fetch')
    await post(endpoint, data)
  }
}

// Gets the referrer as a Location object
function getReferrer(): HTMLAnchorElement | undefined {
  if (document.referrer) {
    const parser = document.createElement('a')
    parser.href = document.referrer
    return parser
  }
  return undefined
}

/**
 * We need to append 'Web' to iOS and Android OS to differentiate between Native Android/iOS events
 * Defaults to 'Web' for non-major browsers
 **/
export function getOperatingSystemName(name?: typeof OperatingSystemName | string) {
  // The string should be uppercase and spaces replaced with underscores
  const normalizeString = name?.toString().toUpperCase().split(' ').join('_')
  switch (normalizeString) {
    case OperatingSystemName.ANDROID:
      return OperatingSystemName.ANDROID_WEB
    case OperatingSystemName.IOS:
      return OperatingSystemName.IOS_WEB
    default: {
      const osName = OperatingSystemName[normalizeString as OperatingSystemName]
      return osName ? osName : OperatingSystemName.WEB
    }
  }
}

function getAnalyticsEventContext(region: string): MessageContext {
  const { browser, device, os } = getDeviceInfo()
  const location = window.location
  const referrer = getReferrer()

  return {
    device: {
      model: device.model,
      manufacturer: device.vendor,
      type: device.type,
      screen_density_dpi: window.devicePixelRatio.toString() || '1',
      screen_height: window.screen.height,
      screen_width: window.screen.width,
    },
    operating_system: {
      name: getOperatingSystemName(os.name),
      version: os.version || 'unknown',
    },
    browser: {
      height: window.innerHeight,
      width: window.innerWidth,
      major_version: browser.major,
      name: browser.name,
      version: browser.version,
    },
    locale: navigator.language,
    user_agent: navigator.userAgent,
    location: {
      country: region,
    },
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    page: {
      path: location.pathname,
      referrer: referrer?.toString(),
      title: '', // This is redacted to prevent PII from leaking
      url: location.href,
      search: location.search,
    },
    // TODO: how do we want to access this information?
    // application: {
    //   name: '',
    //   version: '',
    //   namespace: '',
    // },
    // library: {
    //   name: '',
    //   version: '',
    // },
  }
}
